import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError } from "@/core/api/apiTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/shared/components/shadcn/Dialog";
import { Input } from "@/shared/components/shadcn/Input";
import { Switch } from "@/shared/components/shadcn/Switch";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useUpdateBusinessWebsite, useUploadWebsiteLogo } from "../hooks/businessWebsiteQueries";
import { WebsiteShortNameSchema, type BusinessWebsite } from "../types/businessWebsiteTypes";
import { getBookingWebsiteOpenUrl, getBookingWebsiteUrl } from "../utils/websiteUrl";
import { BookingLinkInput } from "./BookingLinkInput";

interface BusinessWebsiteEditProps {
  businessId: string;
  businessName: string;
  website: BusinessWebsite | null;
  refreshError?: string;
  onRetry: () => void;
}

export function BusinessWebsiteEdit({ businessId, businessName, website, refreshError, onRetry }: BusinessWebsiteEditProps) {
  const [saved, setSaved] = useState(website);
  const [enabled, setEnabled] = useState(website?.enabled ?? false);
  const [shortName, setShortName] = useState(website?.shortName ?? "");
  const [description, setDescription] = useState(website?.description ?? "");
  const [logoSrc, setLogoSrc] = useState(website?.logoSrc ?? null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [readingLogo, setReadingLogo] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [shortNameError, setShortNameError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmLink, setConfirmLink] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const shortNameInput = useRef<HTMLInputElement>(null);
  const copyInput = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const logoRequest = useRef(0);
  const update = useUpdateBusinessWebsite(businessId);
  const upload = useUploadWebsiteLogo(businessId);
  const busy = readingLogo || upload.isPending || update.isPending;
  const validShortName = WebsiteShortNameSchema.safeParse(shortName).success;
  const linkChanged = !!saved && shortName !== saved.shortName;
  const dirty = enabled !== (saved?.enabled ?? false) || shortName !== (saved?.shortName ?? "") ||
    description !== (saved?.description ?? "") || logoSrc !== (saved?.logoSrc ?? null) || logoFile !== null;
  const bookingUrl = saved ? getBookingWebsiteUrl(saved.shortName) : "";
  const displayedLogo = logoPreview ?? logoSrc;

  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview); }, [logoPreview]);
  useEffect(() => () => { ++logoRequest.current; }, []);

  function clearFeedback() {
    setSaveError("");
    setNotice("");
  }

  function changeEnabled(value: boolean) {
    clearFeedback();
    setEnabled(value);
    if (value && !shortName.trim()) {
      setShortName(businessId);
      setShortNameError("");
    }
  }

  async function chooseLogo(file: File | undefined) {
    if (!file) return;
    clearFeedback();
    setLogoError("");
    const request = ++logoRequest.current;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 2 * 1024 * 1024) {
      setLogoError("Choose a PNG, JPG or WebP image no larger than 2 MB.");
      return;
    }
    setReadingLogo(true);
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.src = url;
    try {
      await image.decode();
    } catch {
      URL.revokeObjectURL(url);
      if (request === logoRequest.current) {
        setLogoError("This image couldn't be opened. Choose a different file.");
        setReadingLogo(false);
      }
      return;
    }
    if (request !== logoRequest.current) { URL.revokeObjectURL(url); return; }
    setLogoFile(file);
    setLogoPreview(url);
    setReadingLogo(false);
  }

  async function save() {
    if (submitting.current) return;
    submitting.current = true;
    clearFeedback();
    try {
      let nextLogo = logoSrc;
      if (logoFile) {
        const uploaded = await upload.mutateAsync(logoFile);
        nextLogo = uploaded.logo_src;
        setLogoSrc(nextLogo);
        setLogoFile(null);
      }
      const result = await update.mutateAsync({
        enabled, shortName, description: description.trim() || null, logoSrc: nextLogo,
      });
      setSaved(result);
      setEnabled(result.enabled);
      setShortName(result.shortName);
      setDescription(result.description ?? "");
      setLogoSrc(result.logoSrc);
      setLogoPreview(null);
      setConfirmLink(false);
      setNotice("Website settings saved.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Couldn't save your website. Try again.";
      if (error instanceof ApiError && error.Status === 409) {
        setShortNameError(message);
        setConfirmLink(false);
        shortNameInput.current?.focus();
      } else {
        setSaveError(message);
      }
    } finally {
      submitting.current = false;
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || submitting.current) return;
    clearFeedback();
    const parsed = WebsiteShortNameSchema.safeParse(shortName);
    if (!parsed.success) {
      setShortNameError(parsed.error.issues[0].message);
      shortNameInput.current?.focus();
      return;
    }
    setShortNameError("");
    if (linkChanged) setConfirmLink(true);
    else void save();
  }

  async function copyLink() {
    if (!saved) return;
    if (!navigator.clipboard?.writeText) { setCopyOpen(true); return; }
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setNotice("Booking link copied.");
    } catch {
      setCopyOpen(true);
    }
  }

  return (
    <>
      <FormPage
        header={<PageHeader title="Website" />}
        onSubmit={submit}
        contentClassName="mx-auto w-full max-w-[660px]"
        actions={<Button type="submit" size="touch" disabled={busy || !dirty}>{busy ? "Saving..." : "Save changes"}</Button>}
      >
        {refreshError && <ErrorState title="Couldn't refresh website" message={`${refreshError} Your changes have been kept.`} onRetry={onRetry} />}
        <InfoRow
          title="Enable website"
          caption={enabled ? "Customers can book online after saving." : "Online booking is disabled after saving."}
          actions={<Switch size="lg" aria-label="Enable website" checked={enabled} disabled={busy} onCheckedChange={changeEnabled} />}
        />
        <Section title="Your booking link" card>
          <FieldGroup disabled={busy} className="rounded-none border-0 bg-transparent p-0 sm:p-0">
            <FormField
              id="website-short-name" label="Short name" required error={shortNameError}
              description="3-40 lowercase letters, numbers or single hyphens. This changes your booking URL, not your business name."
            >
              <BookingLinkInput
                ref={shortNameInput} name="shortName" value={shortName} required maxLength={40}
                autoComplete="off" autoCapitalize="none" spellCheck={false}
                onChange={(event) => { clearFeedback(); setShortNameError(""); setShortName(event.target.value); }}
              />
            </FormField>
            {linkChanged && <p className="rounded-xl bg-warning-foreground p-3 text-xs leading-relaxed text-warning">Changing your link may break links you've shared. Update your profiles, messages and printed materials.</p>}
            <div className="grid grid-cols-2 gap-2.5">
              <Button variant="outline" disabled={!saved || linkChanged || !validShortName} onClick={() => void copyLink()}>Copy link</Button>
              {saved && !linkChanged && validShortName ? (
                <Button variant="outline" asChild>
                  <a href={getBookingWebsiteOpenUrl(saved.shortName)} target="_blank" rel="noopener noreferrer">Open link<span className="sr-only"> in a new tab</span></a>
                </Button>
              ) : <Button variant="outline" disabled>Open link</Button>}
            </div>
            {(!saved || linkChanged) && <p className="text-xs text-muted-foreground">Save your booking link before copying or opening it.</p>}
          </FieldGroup>
        </Section>
        <Section title="Your booking page" card>
          <FieldGroup disabled={busy} className="rounded-none border-0 bg-transparent p-0 sm:p-0">
            <FormField id="website-logo" label="Business logo" optional error={logoError} description="PNG, JPG or WebP, up to 2 MB. 600 x 240 recommended. Your logo won't be cropped.">
              <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(event) => { void chooseLogo(event.target.files?.[0]); event.target.value = ""; }} />
            </FormField>
            <div className="flex aspect-[5/2] w-full max-w-[300px] items-center justify-center overflow-hidden rounded-2xl border border-border bg-white">
              {displayedLogo ? <img src={displayedLogo} alt={`${businessName} logo`} className="size-full object-contain p-3" onError={() => setLogoError("Your logo couldn't be displayed. Try uploading it again.")} /> : <span className="text-sm text-muted-foreground">Your logo here</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => fileInput.current?.click()}>{displayedLogo ? "Change logo" : "Upload logo"}</Button>
              <Button variant="outline" disabled={!displayedLogo || busy} aria-label="Remove logo" onClick={() => {
                ++logoRequest.current;
                clearFeedback();
                setLogoError("");
                setLogoFile(null);
                setLogoSrc(null);
                setLogoPreview(null);
              }}>Remove</Button>
            </div>
            <FormField id="website-description" label="Business description" optional>
              <TextArea name="description" value={description} rows={4} maxLength={300} onChange={(event) => { clearFeedback(); setDescription(event.target.value); }} />
            </FormField>
            <p className="text-right text-xs text-muted-foreground">{description.length}/300</p>
          </FieldGroup>
        </Section>
        {saveError && <ErrorState title="Couldn't save website" message={`${saveError} Your changes have been kept.`} />}
        <p role="status" aria-live="polite" className="text-sm text-primary">{notice}</p>
      </FormPage>
      <ConfirmationDialog
        open={confirmLink} onOpenChange={setConfirmLink}
        title="Change your booking link?"
        description={`Your old link may stop working. The new link will be ${getBookingWebsiteUrl(shortName)}. Update it anywhere you've shared it.`}
        confirmLabel="Save new link" dismissLabel="Keep editing" pending={busy} error={saveError}
        onConfirm={() => void save()}
      />
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent onOpenAutoFocus={(event) => { event.preventDefault(); copyInput.current?.focus(); copyInput.current?.select(); }}>
          <DialogHeader>
            <DialogTitle>Copy your booking link</DialogTitle>
            <DialogDescription>Automatic copying isn't available. Select and copy the link below.</DialogDescription>
          </DialogHeader>
          <FormField id="website-copy-url" label="Booking link"><Input ref={copyInput} value={bookingUrl} readOnly /></FormField>
        </DialogContent>
      </Dialog>
    </>
  );
}
